import { memo, useMemo, Component, HTMLAttributes, ReactNode } from 'react';
import { Streamdown } from 'streamdown';
import { cn } from '@/lib/utils';

// Custom components with consistent sizing for chat context
// Using proper typing for Streamdown components
type HeadingProps = HTMLAttributes<HTMLHeadingElement> & { node?: unknown };
type PreProps = HTMLAttributes<HTMLPreElement> & { node?: unknown };
type CodeProps = HTMLAttributes<HTMLElement> & { node?: unknown };

// Types for list components
type ListProps = HTMLAttributes<HTMLElement> & { node?: unknown };

const customComponents = {
  // Headings - commented out to use Streamdown defaults
  // h1: ({ children, ...props }: HeadingProps) => (
  //   <h1 className="text-xl font-semibold text-foreground mt-2 mb-1" {...props}>{children}</h1>
  // ),
  // h2: ({ children, ...props }: HeadingProps) => (
  //   <h2 className="text-lg font-semibold text-foreground mt-2 mb-1" {...props}>{children}</h2>
  // ),
  // h3: ({ children, ...props }: HeadingProps) => (
  //   <h3 className="text-base font-semibold text-foreground mt-2 mb-1" {...props}>{children}</h3>
  // ),
  // h4: ({ children, ...props }: HeadingProps) => (
  //   <h4 className="text-base font-semibold text-foreground mt-2 mb-1" {...props}>{children}</h4>
  // ),
  // h5: ({ children, ...props }: HeadingProps) => (
  //   <h5 className="text-base font-semibold text-foreground mt-2 mb-1" {...props}>{children}</h5>
  // ),
  // h6: ({ children, ...props }: HeadingProps) => (
  //   <h6 className="text-base font-semibold text-foreground mt-2 mb-1" {...props}>{children}</h6>
  // ),
  // Lists - tighter spacing
  ol: ({ children, className, ...props }: ListProps) => (
    <ol className={cn("list-inside list-decimal whitespace-normal my-1 pl-0", className)} {...props}>{children}</ol>
  ),
  ul: ({ children, className, ...props }: ListProps) => (
    <ul className={cn("list-inside list-disc whitespace-normal my-1 pl-0", className)} {...props}>{children}</ul>
  ),
  li: ({ children, className, ...props }: ListProps) => (
    <li className={cn("pb-5 my-0 [&>p]:inline", className)} {...props}>{children}</li>
  ),
  // Links - monochrome + underlined. Overriding the `a` element here (rather than
  // prose-a: classes) is required because Streamdown styles links itself, which
  // prose utilities can't override. `!text-foreground` beats any inherited color.
  a: ({
    children,
    className,
    href,
    ...props
  }: HTMLAttributes<HTMLAnchorElement> & { href?: string; node?: unknown }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "!text-foreground font-medium underline underline-offset-2 decoration-muted-foreground/50 hover:decoration-foreground break-words",
        className,
      )}
      {...props}
    >
      {children}
    </a>
  ),
  // Code blocks - smaller font, better contrast, constrained width
  pre: ({ children, className, ...props }: PreProps) => (
    <pre
      className={cn(
        "text-xs leading-relaxed rounded-lg p-3 my-2 max-w-full whitespace-pre-wrap break-words",
        "bg-zinc-900 dark:bg-zinc-900 text-zinc-100",
        className
      )}
      {...props}
    >
      {children}
    </pre>
  ),
  // Inline code - smaller, better contrast
  code: ({ children, className, ...props }: CodeProps) => {
    // Check if this is inside a pre (code block) or inline
    const isInline = !className?.includes('language-');
    if (isInline) {
      return (
        <code
          className="text-xs px-1.5 py-0.5 rounded bg-black/10 dark:bg-white/10 font-mono break-words"
          {...props}
        >
          {children}
        </code>
      );
    }
    // For code blocks, break-words only breaks long unbreakable strings (URLs), not normal words
    return <code className={cn(className, "break-words")} {...props}>{children}</code>;
  },
  // Streamdown renders raw HTML (via rehype-raw), so chat content that contains tag-like
  // text - e.g. a pasted React component stack with `<MenuItem>` - gets parsed as real HTML.
  // The HTML parser lowercases tag names (`<MenuItem>` -> `<menuitem>`) and treats `menuitem`
  // / `keygen` as normal elements that can hold children, but React lists them as VOID
  // elements and throws (React error #137) when they receive children - crashing the chat.
  // Override these void-mismatch tags to render their children inertly so they never throw.
  menuitem: ({ children }: { children?: ReactNode }) => <span>{children}</span>,
  keygen: ({ children }: { children?: ReactNode }) => <span>{children}</span>,

  // SECURITY: Streamdown's rehype-raw turns raw HTML in a message into LIVE DOM in our own
  // document, and Streamdown's only built-in hardener (rehype-harden) guards link/image URLs
  // but does NOT strip document/style/embedding tags. So when a user pastes a full page
  // (e.g. a reference design starting with `<!DOCTYPE html>...<style>...`), that `<style>`
  // block mounts into OptiDev's <head> and its unscoped selectors (header{}, button{}, body{})
  // repaint the editor chrome. React already inerts `<script>`/string handlers/javascript:
  // hrefs, so the remaining escape surface is these tags. Neutralize them:
  //  - style/script/link/meta/base/title -> render nothing (no global CSS/JS/metadata leak)
  //  - iframe/object/embed/frame/frameset -> render nothing (no embedded content)
  //  - html/head/body -> unwrap to a fragment so the user's *visible text* still shows inert
  style: () => null,
  script: () => null,
  noscript: () => null,
  link: () => null,
  meta: () => null,
  base: () => null,
  title: () => null,
  iframe: () => null,
  object: () => null,
  embed: () => null,
  frame: () => null,
  frameset: () => null,
  html: ({ children }: { children?: ReactNode }) => <>{children}</>,
  head: () => null,
  body: ({ children }: { children?: ReactNode }) => <>{children}</>,
};

/**
 * Error boundary so a single unrenderable message can never take down the whole chat
 * (and therefore the editor). Falls back to showing the raw content as plain text.
 * Resets automatically when the content changes (e.g. streaming or a new message).
 */
interface MarkdownErrorBoundaryProps {
  fallbackContent: string;
  children: ReactNode;
}

interface MarkdownErrorBoundaryState {
  hasError: boolean;
}

class MarkdownErrorBoundary extends Component<MarkdownErrorBoundaryProps, MarkdownErrorBoundaryState> {
  state: MarkdownErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): MarkdownErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: MarkdownErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.fallbackContent !== this.props.fallbackContent) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: unknown): void {
    console.error('[MarkdownRenderer] Failed to render content, falling back to plain text:', error);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="whitespace-pre-wrap break-words text-sm text-foreground">
          {this.props.fallbackContent}
        </div>
      );
    }
    return this.props.children;
  }
}

interface MarkdownRendererProps {
  content: string;
  className?: string;
  isStreaming?: boolean;
}

/**
 * Minimal preprocessing to fix sentences running together without line breaks.
 * Only handles cases where punctuation is immediately followed by a capital letter.
 * Uses negative lookbehind to avoid breaking numbered lists (e.g., "1. Item").
 */
function preprocessContent(content: string): string {
  let processed = content;

  // Add line breaks after sentences that run together (e.g., "sentence one.Sentence two")
  // (?<!\d) = negative lookbehind to skip numbered list items like "1. Item"
  processed = processed.replace(/(?<!\d)\.([A-Z])/g, '.\n\n$1');
  processed = processed.replace(/:([A-Z])/g, ':\n\n$1');

  return processed;
}

/**
 * Industry-standard markdown renderer using Streamdown (by Vercel)
 * - Designed for AI streaming content
 * - Handles incomplete markdown blocks during streaming
 * - GFM support (tables, task lists, strikethrough)
 * - Syntax highlighting with Shiki
 * - Security hardened against prompt injection
 */
export const MarkdownRenderer = memo(function MarkdownRenderer({
  content,
  className,
  isStreaming = false,
}: MarkdownRendererProps) {
  // Preprocess and memoize the content
  const processedContent = useMemo(() => preprocessContent(content), [content]);

  if (!processedContent || !processedContent.trim()) {
    return null;
  }

  return (
    <div
      className={cn(
        'prose prose-sm dark:prose-invert max-w-none overflow-hidden',
        // Heading styles - all same size for chat context consistency
        'prose-headings:text-foreground prose-headings:font-semibold prose-headings:text-sm',
        'prose-h1:mt-2 prose-h1:mb-1',
        'prose-h2:mt-2 prose-h2:mb-1',
        'prose-h3:mt-2 prose-h3:mb-1',
        'prose-h4:mt-2 prose-h4:mb-1',
        // Text styles
        'prose-p:text-foreground prose-p:my-2 prose-p:leading-relaxed [&>p:first-child]:mt-0 [&>p:last-child]:mb-0',
        'prose-strong:text-foreground prose-strong:font-semibold',
        // List styles - controlled via custom components, just color here
        'prose-li:text-foreground',
        // Code styles
        'prose-code:text-foreground prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-normal',
        'prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:rounded-lg',
        // Link styles — monochrome + underlined (theme-aware, no clashing color)
        'prose-a:text-foreground prose-a:font-medium prose-a:underline prose-a:underline-offset-2 prose-a:decoration-muted-foreground/50 hover:prose-a:decoration-foreground',
        // Table styles
        'prose-table:border-collapse prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-muted',
        'prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2',
        className
      )}
    >
      <MarkdownErrorBoundary fallbackContent={processedContent}>
        <Streamdown
          mode="streaming"
          parseIncompleteMarkdown={isStreaming}
          shikiTheme={['github-dark', 'github-light']}
          components={customComponents}
          controls={{
            code: true,
            table: false,
            mermaid: false,
          }}
        >
          {processedContent}
        </Streamdown>
      </MarkdownErrorBoundary>
    </div>
  );
});

export default MarkdownRenderer;
